import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

import type { ValidationError } from 'api/quote-marketplace';
import { STEP_LABELS } from '../../constants';

export interface UnconfirmedVehicleError {
  id: number;
  label: string;
  missingAccidentBenefits: boolean;
  missingAllCoverages: boolean;
}

interface ValidationErrorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
  unconfirmedVehicles?: UnconfirmedVehicleError[];
  onNavigateToTab: (tab: string) => void;
}

const TAB_LABELS: Record<string, string> = {
  ...STEP_LABELS,
  policy: 'Policy Info',
  driver: 'Drivers',
  location: 'Location',
};

const SECTION_LABELS: Record<string, string> = {
  personal_info: 'Personal Info',
  licensing: 'Licensing',
  occupation: 'Occupation',
  insurance_details: 'Insurance Details',
  incidents: 'Incidents',
  garaging: 'Garaging',
  coverages: 'Coverages',
  distance: 'Distance',
  value: 'Value',
  insurance_history: 'Insurance History',
  location: 'Location',
};

const ValidationErrorsModal = ({
  isOpen,
  onClose,
  errors,
  unconfirmedVehicles = [],
  onNavigateToTab,
}: ValidationErrorsModalProps) => {
  const errorBg = useColorModeValue('red.50', 'red.900');
  const errorBorder = useColorModeValue('red.200', 'red.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const hasUnconfirmedVehicles = unconfirmedVehicles.length > 0;

  const groupedErrors = errors.reduce(
    (acc, error) => {
      const tab = error.tab || 'other';
      if (!acc[tab]) {
        acc[tab] = [];
      }
      acc[tab].push(error);
      return acc;
    },
    {} as Record<string, ValidationError[]>,
  );

  const TAB_TO_STEP_KEY: Record<string, string> = {
    location: 'locations',
    'policy-info': 'policy-info',
    policy: 'policy-info',
    additional: 'additional',
  };

  const handleNavigate = (tab: string) => {
    const stepKey = TAB_TO_STEP_KEY[tab] || tab;
    onNavigateToTab(stepKey);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={WarningIcon} color="orange.500" />
            <Text>Missing Required Information</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text mb={4} color={labelColor}>
            Please complete the following required fields before submitting for
            quotes:
          </Text>

          <VStack spacing={4} align="stretch">
            {hasUnconfirmedVehicles && (
              <Box
                bg={errorBg}
                borderWidth="1px"
                borderColor={errorBorder}
                borderRadius="md"
                p={3}
              >
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="600" fontSize="sm">
                    Coverages Confirmation Required
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    variant="link"
                    onClick={() => handleNavigate('vehicle')}
                  >
                    Go to tab
                  </Button>
                </HStack>

                <VStack spacing={1} align="stretch">
                  {unconfirmedVehicles.map((vehicle) => (
                    <Box key={vehicle.id}>
                      {vehicle.missingAccidentBenefits && (
                        <HStack fontSize="sm" spacing={2}>
                          <Text color="red.500">-</Text>
                          <Text>
                            Accident Benefits must be reviewed and confirmed
                            <Text as="span" color={labelColor}>
                              {' '}
                              ({vehicle.label})
                            </Text>
                          </Text>
                        </HStack>
                      )}
                      {vehicle.missingAllCoverages && (
                        <HStack fontSize="sm" spacing={2}>
                          <Text color="red.500">-</Text>
                          <Text>
                            All Coverages must be reviewed and confirmed
                            <Text as="span" color={labelColor}>
                              {' '}
                              ({vehicle.label})
                            </Text>
                          </Text>
                        </HStack>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}

            {Object.entries(groupedErrors).map(([tab, tabErrors]) => (
              <Box
                key={tab}
                bg={errorBg}
                borderWidth="1px"
                borderColor={errorBorder}
                borderRadius="md"
                p={3}
              >
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="600" fontSize="sm">
                    {TAB_LABELS[tab] || tab}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    variant="link"
                    onClick={() => handleNavigate(tab)}
                  >
                    Go to tab
                  </Button>
                </HStack>

                <VStack spacing={1} align="stretch">
                  {tabErrors.map((error, idx) => (
                    <Box key={idx}>
                      <HStack fontSize="sm" spacing={2}>
                        <Text color="red.500">-</Text>
                        <Text>
                          {error.section && SECTION_LABELS[error.section] && (
                            <Text as="span" fontWeight="500">
                              {SECTION_LABELS[error.section]}:{' '}
                            </Text>
                          )}
                          {error.message}
                          {error.driver_id && (
                            <Text as="span" color={labelColor}>
                              {' '}
                              (Driver {error.driver_id})
                            </Text>
                          )}
                          {error.vehicle_id && (
                            <Text as="span" color={labelColor}>
                              {' '}
                              (Vehicle {error.vehicle_id})
                            </Text>
                          )}
                        </Text>
                      </HStack>
                      {error.required_by_markets &&
                        error.required_by_markets.length > 0 && (
                          <Text fontSize="xs" color="red.600" ml={4}>
                            Required by: {error.required_by_markets.join(', ')}
                          </Text>
                        )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ValidationErrorsModal;
